import React, { useContext, useEffect, useState } from "react";
import { Content } from "antd/es/layout/layout";
import { Card, Col, Row, Spin, Badge, Tooltip, Tag } from "antd";
import { AuthContext } from "../../../config/AuthContext";
import Login from "../../Login";
import { GetJavadex } from "../../../dao/jutge/GetJavadex";
import { GetJavamonConfig, getJavamonImage } from "../../../dao/jutge/GetJavamonConfig";
import { BookOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";

interface JavadexItem {
    id: number;
    nom: string;
    image: string;
    descr: string;
    rarity: string;
    seen: number;     // 1 or 0
    captured: number; // 1 or 0
}

const fallbackRarityStyle: Record<string, { color: string; }> = {
    normal: { color: "#555" },
    epic: { color: "#f1c40f" },
    legendary: { color: "#9b59b6" },
};

interface JavadexProps {
    isGameModal?: boolean;
}

const Javadex: React.FC<JavadexProps> = ({ isGameModal = false }) => {
    const { isLoggedIn, userId, setBreadCrumb } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const isInGameContext = isGameModal || location.pathname.startsWith("/game");

    // Proteger si se accede mediante /game/javadex: debe ser dentro de un iframe
    useEffect(() => {
        if (isInGameContext) {
            const isInsideIframe = window.self !== window.top;
            if (!isInsideIframe) {
                navigate("/home", { replace: true });
            }
        }
    }, [isInGameContext, navigate]);

    const [loading, setLoading] = useState(true);
    const [javamons, setJavamons] = useState<JavadexItem[]>([]);
    const [rarityMap, setRarityMap] = useState<Record<string, string>>({});

    useEffect(() => {
        if (isLoggedIn) loadData();
        if (!isInGameContext) {
            setBreadCrumb(
                [
                    { nom: "Home", nav: "/home" },
                    { nom: "JAVAdex", nav: "/javadex" },
                ],
                true
            );
        }
    }, [isLoggedIn, userId, isInGameContext]);

    const loadData = async () => {
        setLoading(true);
        try {
            const raw = await GetJavadex(userId + "");
            let data: JavadexItem[] = [];

            try {
                const cfgData = await GetJavamonConfig();
                if (cfgData) {
                    const rList = cfgData.rarities && cfgData.rarities !== "null" ? JSON.parse(cfgData.rarities) : [];
                    const rMap: Record<string, string> = {};
                    rList.forEach((r: any) => {
                        const n = r.nom?.toLowerCase() || '';
                        rMap[n] = r.color;
                        rMap[r.nom] = r.color;
                        if (n === 'épico' || n === 'epico') rMap['epic'] = r.color;
                        if (n === 'legendario' || n === 'leyenda') rMap['legendary'] = r.color;
                        if (n === 'normal' || n === 'comun' || n === 'común') rMap['normal'] = r.color;
                    });
                    setRarityMap(rMap);
                }
            } catch (err) {
                console.error("Error cargando colores de rareza:", err);
            }

            if (raw && raw.json) {
                // If wrapper
                data = JSON.parse(raw.json);
            } else if (Array.isArray(raw)) {
                data = raw;
            } else if (typeof raw === 'string') {
                try { data = JSON.parse(raw); } catch { }
            }
            // Sort by name case-insensitive
            data.sort((a, b) => a.nom.localeCompare(b.nom));

            setJavamons(data || []);
        } catch (e) {
            console.error("Error loading Javadex:", e);
        } finally {
            setLoading(false);
        }
    };

    const javamonImage = (jm: any) => {
        return getJavamonImage(jm);
    };

    if (!isLoggedIn) return <Login />;

    if (loading) return (
        <Content style={{ padding: 40, textAlign: "center" }}>
            <Spin size="large" />
        </Content>
    );

    return (
        <Content style={{ padding: "24px", background: "#f0f2f5", minHeight: "100vh" }}>
            <Card
                title={<span><BookOutlined style={{ marginRight: 8 }} /> JAVAdex</span>}
                style={{ borderRadius: 16, boxShadow: "0 6px 20px rgba(0,0,0,0.08)" }}
            >
                <Row gutter={[24, 24]}>
                    {javamons.map((jm) => {
                        const isSeen = jm.seen === 1;
                        // Determine styles based on seen status
                        const imgStyle: React.CSSProperties = {
                            width: "100%",
                            maxHeight: 140,
                            objectFit: "contain",
                            marginBottom: 12,
                            filter: isSeen ? "none" : "brightness(0) grayscale(100%) opacity(0.7)"
                        };

                        // Description handling: only if seen
                        const DescriptionContent = isSeen ? (
                            <span style={{ fontSize: 12, color: "#666" }}>{jm.descr}</span>
                        ) : null;

                        // If seen, wrap in Tooltip for description.
                        // "si pasa el ratón por encima ... se muestra lo que hacen"
                        const cardContent = (
                            <Card
                                hoverable
                                style={{
                                    borderRadius: 16,
                                    textAlign: "center",
                                    background: isSeen
                                        ? "linear-gradient(180deg, #ffffff 0%, #f7f7f7 100%)"
                                        : "#eee",
                                    borderColor: isSeen ? undefined : "#ccc"
                                }}
                            >
                                <img
                                    src={javamonImage(jm)}
                                    alt={jm.nom}
                                    style={imgStyle}
                                />
                                <h4 style={{
                                    marginBottom: 4,
                                    color: isSeen ? (rarityMap[jm.rarity] || rarityMap[jm.rarity?.toLowerCase()] || fallbackRarityStyle[jm.rarity]?.color || "#555") : "#555",
                                    filter: isSeen ? "none" : "grayscale(100%)"
                                }}>
                                    {jm.nom}
                                </h4>

                                <div style={{ fontSize: 11, textTransform: "uppercase", color: isSeen ? (rarityMap[jm.rarity] || rarityMap[jm.rarity?.toLowerCase()] || fallbackRarityStyle[jm.rarity]?.color || "#999") : "#999" }}>
                                    {jm.rarity}
                                </div>

                                {jm.captured === 1 && isSeen && (
                                    <div style={{ marginTop: 5 }}>
                                        <Badge status="success" text="Capturado" />
                                    </div>
                                )}
                                {isSeen && jm.descr && jm.descr.includes('PROBLEMA') && (
                                    <Tooltip title="Puede revelar los fallos obtenidos en un único test de una actividad entregada">
                                        <Tag color="cyan" style={{ marginTop: 5 }}>PROBLEMA</Tag>
                                    </Tooltip>
                                )}
                                {isSeen && jm.descr && jm.descr.includes('TIEMPO') && (
                                    <Tooltip title="Se puede gastar su carga para ampliar 24h una entrega de actividad">
                                        <Tag color="purple" style={{ marginTop: 5 }}>TIEMPO</Tag>
                                    </Tooltip>
                                )}
                                {isSeen && jm.descr && jm.descr.includes('NOTA') && (
                                    <Tooltip title="Sube 0.5 puntos la nota de la actividad (siempre que no sea ya del 100%)">
                                        <Tag color="gold" style={{ marginTop: 5 }}>NOTA</Tag>
                                    </Tooltip>
                                )}
                                {isSeen && jm.descr && jm.descr.includes('CANDADO') && (
                                    <Tooltip title={`Puedes desbloquear un candado por Actividad accediendo así a su contenido antes que el resto.`}>
                                        <Tag color="green" style={{ marginTop: 5 }}>CANDADO</Tag>
                                    </Tooltip>
                                )}
                                {isSeen && jm.descr && jm.descr.includes('TEST') && (
                                    <Tooltip title={`Revela TODOS los tests de un ejercicio por Actividad.`}>
                                        <Tag color="blue" style={{ marginTop: 5 }}>TESTS</Tag>
                                    </Tooltip>
                                )}
                            </Card>
                        );

                        return (
                            <Col key={jm.id} xs={24} sm={12} md={8} lg={6}>
                                {isSeen && jm.descr ? (
                                    <Tooltip>
                                        {cardContent}
                                    </Tooltip>
                                ) : (
                                    cardContent
                                )}
                            </Col>
                        );
                    })}

                    {javamons.length === 0 && <div style={{ padding: 20 }}>No Javamons found in database.</div>}
                </Row>
            </Card>
        </Content>
    );
};

export default Javadex;
